import { z } from "zod";

import type { Language } from "@rootreeweb/multi-language";
import type { Entity } from "@rootreeweb/plugin-lib";

export interface ICategory extends Entity {
	fields: string;
	label_en: string;
	label_fr: string;
}

export interface IQuestion extends Entity {
	answer_en: string;
	answer_fr: string;
	category_id: string;
	question_en: string;
	question_fr: string;
}
export const faqCategorySchema = z.object({
	added: z.string().optional(),
	deleted: z.string().optional(),
	hide_from_list: z.string().transform((v) => v === "1"),
	id: z.number(),
	label_en: z.string(),
	label_fr: z.string(),
	position: z.string().transform((v) => Number.parseInt(v)),
	user_id: z.string().optional(),
});
export type FAQCategory = z.infer<typeof faqCategorySchema>;
export const faqSchema = z.object({
	added: z.string().optional(),
	answer_en: z.string(),
	answer_fr: z.string(),
	category_id: z.string().transform((v) => Number.parseInt(v)),
	category_id_pretty: z.string().nullable().optional(),
	deleted: z.string().optional(),
	id: z.number(),
	position: z.string().transform((v) => Number.parseInt(v)),
	question_en: z.string(),
	question_fr: z.string(),
	user_id: z.string().optional(),
});

/**
 * Determine if the given string value generated from PHP is truthy.
 *
 * @param value The value from PHP that was cast to a string.
 * @returns True if the value was originally truthy.
 */
function isTruthyFromPHP(value?: string) {
	return value === "1" || value === "true";
}

export const faqPropsSchema = z.object({
	alpha: z.string().default("").transform(isTruthyFromPHP).optional(),
	cat: z
		.string()
		.default("")
		.transform((cat) =>
			cat ? cat.split(",").map((i) => Number.parseInt(i)) : [],
		)
		.optional(),
	categories: z.array(faqCategorySchema),
	class: z.string().default("").optional(),
	hide_accordion: z.string().default("").transform(isTruthyFromPHP).optional(),
	hide_title: z.string().default("").transform(isTruthyFromPHP).optional(),
	lang: z.union([z.literal("en"), z.literal("fr")]),
	options: z.string().optional(),
	questions: z.array(faqSchema),
	show: z.string().default("").transform(isTruthyFromPHP).optional(),
	style: z.string().optional(),
});

/** Permitted attributes for the shortcode tag. */
export const faqAllowedAttributes: (keyof FAQProps)[] = [
	"show",
	"cat",
	"class",
	"style",
	"alpha",
	"hide_title",
	"hide_accordion",
	"options",
] as const;
export type FAQCategoryWithQuestions = FAQCategory & {
	questions: FAQQuestion[];
};
export type FAQProps = z.infer<typeof faqPropsSchema>;
export type FAQQuestion = z.infer<typeof faqSchema>;

export type InteractiveFAQProps = {
	categories: FAQCategoryWithQuestions[];
	faqProps: FAQProps;
	lang: Language;
};

export const datasetSchema = z
	.object({
		dataset: faqPropsSchema.omit({ categories: true, questions: true }).extend({
			categories: z
				.string()
				.transform((s) => faqCategorySchema.array().parse(JSON.parse(s))),
			questions: z
				.string()
				.transform((s) => faqSchema.array().parse(JSON.parse(s))),
		}),
	})
	.transform(({ dataset }) => dataset);
